import React, { useMemo } from "react";

import { Controller, useFormContext } from "react-hook-form";

import { AiNodeQuota, AiNodeV2Form, BasicModelCard } from "src/aiNode/types";
import { Card } from "src/base-components/Card";
import { FormItem } from "src/base-components/FormItem";
import { ModelProvider } from "src/clients/agent-management";
import {
  DEPRECATED_BADGE_CONFIG,
  filterModels,
  isGlobalRouting,
  ModelFilterConfig,
  ModelOption,
  ModelQuota,
  ModelSelectorDropdown,
  TAKTILE_HOSTED_BADGE_CONFIG,
} from "src/genai/ModelSelector";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";

const AI_NODE_ICON_BASE_URL = "https://app.cp.taktile.com";

const MODELS_BEHIND_FEATURE_FLAGS: Record<string, FEATURE_FLAGS> = {};

const computeModelVisibility = (
  models: BasicModelCard[],
): Record<string, boolean> => {
  const values: Record<string, boolean> = {};

  // Per-model feature flags
  Object.entries(MODELS_BEHIND_FEATURE_FLAGS).map(([modelId, flag]) => {
    values[modelId] = isFeatureFlagEnabled(flag);
  });

  // WIP models share one flag
  const showWip = isFeatureFlagEnabled(
    FEATURE_FLAGS.aiNodeWorkInProgressModels,
  );
  models
    .filter((m) => m.status === "wip")
    .map((m) => {
      values[m.model_id] = showWip;
    });

  return values;
};

type ModelSelectorProps = {
  models: BasicModelCard[];
  quotas: AiNodeQuota;
  immutable: boolean;
};

export const ModelSelector: React.FC<ModelSelectorProps> = ({
  models,
  quotas,
  immutable,
}) => {
  const { setValue, watch, control } = useFormContext<AiNodeV2Form>();
  const selectedModelId = watch("model.model_id");

  // Compute model visibility (feature flags + WIP)
  // Not memoized because isFeatureFlagEnabled reads from URL params which can change mid-session
  const modelVisibility = computeModelVisibility(models);

  // Build filter config for the generic filtering utility
  const filterConfig: ModelFilterConfig = useMemo(
    () => ({
      quotas: convertAiNodeQuotasToModelQuotas(quotas),
      selectedModelId,
      modelVisibility,
      quotaDisabledReason:
        "This feature requires the AI Workbench package. Please contact your Solution Engineer to obtain access to this model.",
    }),
    [quotas, selectedModelId, modelVisibility],
  );

  // Adapt models to generic format, then apply filtering and quota-based availability
  const modelOptions: ModelOption[] = useMemo(() => {
    const allModels = models.map(convertAiNodeBasicModelCardToModelOption);
    return filterModels(allModels, filterConfig);
  }, [models, filterConfig]);

  const handleSelect = (modelId: string) => {
    const modelOption = modelOptions.find((m) => m.id === modelId);
    if (modelOption && !modelOption.disabled) {
      setValue("model", {
        model_id: modelId,
        provider: modelOption.provider,
      });
    }
  };

  return (
    <Card className="w-full pt-4 pb-4">
      <FormItem>
        <FormItem.Label
          className="mb-2"
          description="Pick an AI model for your task. Each model has different capabilities and performance characteristics."
          isRequired
        >
          Select AI model
        </FormItem.Label>
        <Controller
          control={control}
          name="model.model_id"
          render={() => (
            <div className="w-full">
              <ModelSelectorDropdown
                dataLoc="ai-node-model-selector"
                disabled={immutable}
                modelOptions={modelOptions}
                selectedModelId={selectedModelId}
                onSelect={handleSelect}
              />
            </div>
          )}
        />
      </FormItem>
    </Card>
  );
};

const convertAiNodeBasicModelCardToModelOption = (
  model: BasicModelCard,
): ModelOption => {
  const provider = (model.providers?.[0] ?? "bedrock") as ModelProvider;

  return {
    id: model.model_id,
    displayName: model.name,
    description: model.description,
    iconUrl: AI_NODE_ICON_BASE_URL + model.icon_path,
    provider,
    routingTargetRegion: model.routing_target_region,
    badgeConfig: resolveAiNodeBadgeConfig(model, provider),
  };
};

const resolveAiNodeBadgeConfig = (
  model: BasicModelCard,
  provider: ModelProvider,
) => {
  if (model.status === "deprecated") {
    return DEPRECATED_BADGE_CONFIG;
  }
  // Not showing "Taktile-hosted" in regions where routing is to Global
  // since Data Residency is not maintained
  if (provider === "bedrock" && !isGlobalRouting(model.routing_target_region)) {
    return TAKTILE_HOSTED_BADGE_CONFIG;
  }
  return undefined;
};

const convertAiNodeQuotasToModelQuotas = (
  aiNodeQuotas: AiNodeQuota,
): Record<string, ModelQuota> => {
  const result: Record<string, ModelQuota> = {};

  if (aiNodeQuotas.usage) {
    Object.entries(aiNodeQuotas.usage).map(([modelId, usage]) => {
      result[modelId] = {
        tokenQuota: usage.token_quota,
        tokensUsed: usage.tokens_used ?? undefined,
      };
    });
  }

  return result;
};
